import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { summaryType } from "../../../../../services/api/types/reports/reports";
import { useCustomisation } from "../../../../../context/CustomisationContext";

interface AssignmentActivitySummaryTableProps {
  data: summaryType[];
}

export const AssignmentActivitySummaryTable: React.FC<
  AssignmentActivitySummaryTableProps
> = ({ data }) => {
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startScrollLeft: number; trackW: number; thumbW: number } | null>(null);
  const [scrollbar, setScrollbar] = useState({ show: false, thumbWidth: 0, thumbLeft: 0 });
  const SCROLL_ARROW_W = 44;

  const refreshScrollbar = useCallback(() => {
    const el = tableScrollRef.current;
    if (!el || dragRef.current) return;
    const scrollable = el.scrollWidth > el.clientWidth + 1;
    if (!scrollable) { setScrollbar(s => ({ ...s, show: false })); return; }
    const trackW = el.clientWidth - SCROLL_ARROW_W * 2;
    const thumbW = Math.max((el.clientWidth / el.scrollWidth) * trackW, 44);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const thumbLeft = maxScroll > 0 ? (el.scrollLeft / maxScroll) * (trackW - thumbW) : 0;
    setScrollbar({ show: true, thumbWidth: thumbW, thumbLeft });
  }, [SCROLL_ARROW_W]);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", refreshScrollbar, { passive: true });
    const ro = new ResizeObserver(refreshScrollbar);
    ro.observe(el);
    refreshScrollbar();
    return () => { el.removeEventListener("scroll", refreshScrollbar); ro.disconnect(); };
  }, [refreshScrollbar]);

  useEffect(() => {
    const move = (clientX: number) => {
      const d = dragRef.current;
      if (!d) return;
      const el = tableScrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const scale = maxScroll / Math.max(d.trackW - d.thumbW, 1);
      const newScrollLeft = Math.max(0, Math.min(maxScroll, d.startScrollLeft + (clientX - d.startX) * scale));
      el.scrollLeft = newScrollLeft;
      if (thumbRef.current) {
        const newLeft = maxScroll > 0 ? (newScrollLeft / maxScroll) * (d.trackW - d.thumbW) : 0;
        thumbRef.current.style.left = `${newLeft}px`;
      }
    };
    const end = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      refreshScrollbar();
    };
    const onMM = (e: MouseEvent) => move(e.clientX);
    const onTM = (e: TouchEvent) => {
      if (!dragRef.current) return;
      e.preventDefault();
      move(e.touches[0].clientX);
    };
    document.addEventListener("mousemove", onMM);
    document.addEventListener("mouseup", end);
    document.addEventListener("touchmove", onTM, { passive: false });
    document.addEventListener("touchend", end);
    return () => {
      document.removeEventListener("mousemove", onMM);
      document.removeEventListener("mouseup", end);
      document.removeEventListener("touchmove", onTM);
      document.removeEventListener("touchend", end);
    };
  }, [refreshScrollbar]);

  const onThumbDragStart = (clientX: number) => {
    const el = tableScrollRef.current;
    if (!el) return;
    dragRef.current = { startX: clientX, startScrollLeft: el.scrollLeft, trackW: el.clientWidth - SCROLL_ARROW_W * 2, thumbW: scrollbar.thumbWidth };
  };

  const scrollByAmount = (amount: number) => {
    tableScrollRef.current?.scrollBy({ left: amount, behavior: "smooth" });
  };

  const getPercentage = (part: number, total: number) =>
    total ? Math.round((part / total) * 100) : 0;

  const borderColor = "#EAECF0";

  const { customisations } = useCustomisation();
  const {
    HOME: { assignmentStatusColorsSettings },
  } = customisations.customisations;

  const homeCompletedColor =
    assignmentStatusColorsSettings?.homeCompleted || "#5354D8";
  const homePendingColor =
    assignmentStatusColorsSettings?.homePending || "#F79009";
  const homeOverdueColor =
    assignmentStatusColorsSettings?.homeOverdue || "#F04438";

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0px 1px 3px 0px #1018281A",
          p: 3,
        }}
      >
        <Typography color="#475467" fontSize={14} align="center">
          No data available.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        border: `1px solid ${borderColor}`,
        boxShadow: "0px 1px 3px 0px #1018281A",
        p: 0,
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      <TableContainer
        ref={tableScrollRef}
        sx={{
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-x",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Table sx={{ minWidth: { xs: 860, md: "auto" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontSize: 13,
                  color: "#475467",
                  fontWeight: 500,
                  borderBottom: `1px solid ${borderColor}`,
                  backgroundColor: "#F9FAFB",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell
                sx={{
                  maxWidth: { md: 333 },
                  minWidth: { xs: 180, md: "auto" },
                  position: { xs: "sticky", md: "static" },
                  left: { xs: 0, md: "auto" },
                  zIndex: { xs: 2, md: "auto" },
                  backgroundColor: "#F9FAFB",
                }}
              >
                Assignments
              </TableCell>
              <TableCell sx={{ minWidth: { xs: 110, md: "auto" } }}>Assigned to</TableCell>
              <TableCell sx={{ minWidth: { xs: 100, md: "auto" } }}>Avg score</TableCell>
              <TableCell sx={{ minWidth: { xs: 100, md: "auto" } }}>Time (min)</TableCell>
              <TableCell sx={{ minWidth: { xs: 100, md: "auto" } }}>Time (avg)</TableCell>
              <TableCell sx={{ minWidth: { xs: 100, md: "auto" } }}>Time (max)</TableCell>
              <TableCell align="right" sx={{ minWidth: { xs: 290, md: "auto" } }}>Completion rate</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row) => {
              const total = row.assignedUsers;
              const completedPct = getPercentage(row.completedUsers, total);
              const pendingPct = getPercentage(row.pendingUsers, total);
              const overduePct = getPercentage(row.overdueUsers, total);

              const rowBg = "#ffffff";

              return (
                <TableRow
                  key={row.portalAssignmentUid}
                  sx={{
                    backgroundColor: rowBg,
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#F2F4F7",
                    },
                    "& td": {
                      borderBottom: `1px solid ${borderColor}`,
                      fontSize: 13,
                      color: "#475467",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: "#101828",
                      fontWeight: 400,
                      maxWidth: { md: 333 },
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      position: { xs: "sticky", md: "static" },
                      left: { xs: 0, md: "auto" },
                      zIndex: { xs: 1, md: "auto" },
                      backgroundColor: { xs: rowBg, md: "inherit" },
                    }}
                  >
                    {row.title}
                  </TableCell>
                  <TableCell>{row.assignedUsers}</TableCell>
                  <TableCell>
                    {row.avgScore == null ? "-" : `${row.avgScore}%`}
                  </TableCell>
                  <TableCell>
                    {row.minCompleteTime == null ? "-" : row.minCompleteTime}
                  </TableCell>
                  <TableCell>
                    {row.avgCompleteTime == null ? "-" : row.avgCompleteTime}
                  </TableCell>
                  <TableCell>
                    {row.maxCompleteTime == null ? "-" : row.maxCompleteTime}
                  </TableCell>

                  <TableCell align="right">
                    <Stack
                      direction="row"
                      gap={0.75}
                      justifyContent="flex-end"
                      flexWrap={{ xs: "nowrap", md: "wrap" }}
                    >
                      <CompletionBadge
                        color={homeCompletedColor}
                        count={row.completedUsers}
                        pct={completedPct}
                      />
                      <CompletionBadge
                        color={homePendingColor}
                        count={row.pendingUsers}
                        pct={pendingPct}
                      />
                      <CompletionBadge
                        color={homeOverdueColor}
                        count={row.overdueUsers}
                        pct={overduePct}
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {scrollbar.show && (
        <Box sx={{ display: "flex", alignItems: "center", height: SCROLL_ARROW_W, backgroundColor: "#fff", userSelect: "none" }}>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(-100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(-100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="5,1 1,5 5,9" fill="#344054"/></svg>
          </Box>
          <Box sx={{ flex: 1, position: "relative", height: SCROLL_ARROW_W }}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                height: 16,
                width: scrollbar.thumbWidth,
                left: scrollbar.thumbLeft,
                backgroundColor: "#eaecf0",
                borderRadius: "10px",
                cursor: "grab",
                touchAction: "none",
                "&:active": { cursor: "grabbing" },
              }}
              ref={thumbRef}
              onMouseDown={(e) => { e.preventDefault(); onThumbDragStart(e.clientX); }}
              onTouchStart={(e) => { e.preventDefault(); onThumbDragStart(e.touches[0].clientX); }}
            />
          </Box>
          <Box
            sx={{ width: SCROLL_ARROW_W, height: SCROLL_ARROW_W, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
            onMouseDown={() => scrollByAmount(100)}
            onTouchStart={(e) => { e.preventDefault(); scrollByAmount(100); }}
          >
            <svg width="10" height="16" viewBox="0 0 6 10"><polygon points="1,1 5,5 1,9" fill="#344054"/></svg>
          </Box>
        </Box>
      )}

      {/* Legend */}
      <Stack
        direction="row"
        gap={2}
        px={2}
        py={1.5}
        alignItems="center"
        bgcolor="#F9FAFB"
      >
        <LegendItem color={homeCompletedColor} label="Completed" />
        <LegendItem color={homePendingColor} label="Pending" />
        <LegendItem color={homeOverdueColor} label="Overdue" />
      </Stack>
    </Paper>
  );
};

const CompletionBadge: React.FC<{
  color?: string;
  count: number;
  pct: number;
}> = ({ color, count, pct }) => {
  const safeColor = color || "#F2F4F7";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "20px",
        backgroundColor: `${safeColor}1A`,
        border: `1px solid ${alpha(safeColor, 0.3)}`,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: safeColor,
          flexShrink: 0,
        }}
      />
      <Typography
        fontSize={12}
        fontWeight={500}
        color={safeColor}
        whiteSpace="nowrap"
      >
        {count} ({pct}%)
      </Typography>
    </Box>
  );
};

const LegendItem: React.FC<{ color?: string; label: string }> = ({
  color,
  label,
}) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: color,
      }}
    />
    <Typography fontSize={12} color="#475467">
      {label}
    </Typography>
  </Stack>
);
